/**
 * Admin > Multi-factor DEMA-WMA Gainer (TEK_DEMA_WMA_GAINER).
 *
 * Four tabs over the existing panels (a UI reorganisation only -- no rule, execution or calculation changes):
 *   1. Paper & Live Trading  -- live / paper status and controls, positions, orders, trade book, P&L
 *   2. Backtest              -- 1-minute index candle backtests (data source per session, session data quality)
 *   3. Backtest vs Paper     -- comparison chart and metrics (paper trades only; live is never mixed in)
 *   4. Rules in Effect       -- the effective configuration in collapsible sections + version management
 * The overview is loaded once here. Each tab loads its own data when first opened; the trading tab (which polls)
 * is unmounted when left, the other tabs stay mounted once opened so their results are not lost.
 * Saving settings creates a NEW active config version on the server; nothing here enables live trading.
 */
import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import { activationConfirmation, type DemaWmaOverview } from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import { extractErrorMessage } from "@/utils/errors";
import DemaWmaPaperPanel from "./DemaWmaPaperPanel";
import DemaWmaLivePanel from "./DemaWmaLivePanel";
import DemaWmaBacktestCard from "./DemaWmaBacktestCard";
import DemaWmaComparisonCard from "./DemaWmaComparisonCard";
import DemaWmaRulesTab from "./DemaWmaRulesTab";

// Settings helpers moved to ./demaWmaRules (shared with the Rules in Effect tab); re-exported for existing imports.
export { RULE_SET_V2, RULE_SET_V6, SECTIONS, SectionCard, fieldError, rulesSummary, sectionsFor } from "./demaWmaRules";
export type { FieldSpec, SectionSpec } from "./demaWmaRules";

export const TABS = [
  { key: "trading", label: "Paper & Live Trading" },
  { key: "backtest", label: "Backtest" },
  { key: "compare", label: "Backtest vs Paper" },
  { key: "rules", label: "Rules in Effect" },
] as const;
export type TabKey = (typeof TABS)[number]["key"];
/** Tabs kept mounted after their first visit (no polling; their results survive switching tabs). */
const KEEP_MOUNTED: ReadonlySet<TabKey> = new Set<TabKey>(["backtest", "compare", "rules"]);

function tabFromUrl(): TabKey {
  try {
    const t = new globalThis.URLSearchParams(globalThis.location?.search ?? "").get("tab");
    return (TABS.find((x) => x.key === t)?.key ?? "trading") as TabKey;
  } catch {
    return "trading";
  }
}

function writeTabToUrl(tab: TabKey) {
  try {
    const url = new globalThis.URL(globalThis.location.href);
    url.searchParams.set("tab", tab);
    globalThis.history.replaceState(globalThis.history.state, "", url.toString());
  } catch {
    /* non-browser environments: tab still works, just not in the URL */
  }
}

function isForbidden(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

function TradingTab({ data, onChanged }: { data: DemaWmaOverview; onChanged: () => void }) {
  const [showAdminLive, setShowAdminLive] = useState(false);
  const tf = Number(data.config?.timeframe_minutes ?? 5) === 1 ? 1 : 5;
  return (
    <Box>
      <Alert severity={data.live_approved ? "warning" : "info"} sx={{ mb: 2 }}>
        {data.paper_only
          ? `PAPER ONLY: this strategy's own instance trades simulated orders on completed ${tf}-minute index bars, with its own positions, daily trade count and P&L. It can never trade live and cannot be activated by customers.`
          : data.live_approved
          ? `Live trading is approved on v${data.live_trading.approved_version} (${(data.live_trading.instruments || []).join(", ")}, max ${data.live_trading.max_lots} lot(s), daily loss cap ₹${data.live_trading.max_daily_loss}). Real orders are sent only while every gate in the Live trading panel is green; while any gate is off the strategy is NOT evaluated at all (no paper trades either). Signals use completed ${tf}-minute index bars.`
          : data.runner_available
          ? `This page's own instance trades on PAPER (simulated orders) on completed ${tf}-minute index bars. Live trading runs per customer: each customer who clicks Activate on Algo Strategies trades with the same active version on their own broker account (see Customer activations below).`
          : "Configuration only: the paper-trading runner is not deployed yet."}
      </Alert>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" color="info" label="PAPER = simulated orders" />
        <Chip size="small" color="error" label="LIVE = real broker orders" />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
          Every position, order and trade row carries its mode; the trade book and CSV can be filtered by mode.
        </Typography>
      </Stack>
      {data.runner_available && !data.paper_only && (data.live_approved || showAdminLive ? (
        <DemaWmaLivePanel onChanged={onChanged} />
      ) : (
        <Box sx={{ mb: 2 }}>
          <Button size="small" onClick={() => setShowAdminLive(true)} data-testid="show-admin-live">
            Advanced: admin live instance
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Only for running this page&apos;s own instance on a broker account; customer live trading does not need it.
          </Typography>
        </Box>
      ))}
      {data.runner_available
        ? <DemaWmaPaperPanel onChanged={onChanged} />
        : <Alert severity="info" variant="outlined">No paper or live trading data: the runner is not deployed on this server.</Alert>}
    </Box>
  );
}

/** Which strategy this page shows (key, parent, paper-only) and its paper activity today. */
function StrategyIdentity({ data }: { data: DemaWmaOverview }) {
  const t = data.today;
  const inr = (v: number) => `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap alignItems="center" data-testid="strategy-identity">
      <Chip size="small" variant="outlined" label={`Key ${data.strategy_key}`} />
      {data.parent && <Chip size="small" variant="outlined" color="secondary" label={`Forked from ${data.parent.strategy_key} v${data.parent.version}`} />}
      {data.paper_only && <Chip size="small" color="info" label="PAPER ONLY" />}
      <Chip size="small" variant="outlined" label={`Runner ${data.execution_status === "LIVE" ? "ON" : "OFF"}`} />
      {t && (
        <Typography variant="caption" color="text.secondary" data-testid="today-summary">
          Today ({t.date}): entries {t.entries} / {t.max_trades_per_day ?? "unlimited"} · open positions {t.open_positions} ·
          closed net P&amp;L {inr(t.closed_net_pnl)}
        </Typography>
      )}
    </Stack>
  );
}

export default function DemaWmaGainerPage() {
  const demaWmaGainerService = useDemaWmaService();
  const [data, setData] = useState<DemaWmaOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTabState] = useState<TabKey>(tabFromUrl);
  const [visited, setVisited] = useState<Set<TabKey>>(() => new Set([tabFromUrl()]));

  const setTab = (t: TabKey) => {
    setTabState(t);
    setVisited((v) => (v.has(t) ? v : new Set(v).add(t)));
    writeTabToUrl(t);
  };

  const load = useCallback(async () => {
    try {
      setData(await demaWmaGainerService.getOverview());
      setError(null);
    } catch (e) {
      setForbidden(isForbidden(e));
      setError(extractErrorMessage(e));
    }
  }, [demaWmaGainerService]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveDraft = async () => {
    try {
      const r = await demaWmaGainerService.saveV2Draft();
      setNotice(`Saved v${r.version} as an INACTIVE v2 draft. Nothing was activated.`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const activate = async (version: number) => {
    const expected = activationConfirmation(version);
    const typed = globalThis.prompt(`Activating v${version} changes the rules used by the paper runner. Type exactly:\n${expected}`);
    if (typed === null) return;
    try {
      const r = await demaWmaGainerService.activateVersion(version, typed);
      setNotice(`v${r.active_version} is now active (was v${r.previous_version ?? "—"}). Start/stop state unchanged.`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  if (forbidden && !data) {
    return <Alert severity="warning" sx={{ m: 2 }} data-testid="permission-denied">You do not have permission to view this strategy (admin only).</Alert>;
  }
  if (error && !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={() => void load()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }
  if (!data) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress aria-label="Loading strategy" /></Box>;
  const activeTf = Number(data.config?.timeframe_minutes ?? 5) === 1 ? 1 : 5;
  const show = (t: TabKey) => tab === t || (KEEP_MOUNTED.has(t) && visited.has(t));

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h5" fontWeight={700}>{data.algo_name}</Typography>
        {data.live_approved
          ? <Chip label="LIVE APPROVED" color="error" size="small" />
          : <Chip label="PAPER" color="info" size="small" />}
        <Chip label={`Status: ${data.execution_status}`} size="small" variant="outlined" />
        <Chip label={`Config v${data.active_version ?? "—"}`} size="small" variant="outlined" />
        <Typography variant="caption" color="text.secondary" title={data.config_hash}>hash {data.config_hash.slice(0, 10)}…</Typography>
      </Stack>
      <StrategyIdentity data={data} />

      {notice && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {data.validation_errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>Active config is invalid: {data.validation_errors.join("; ")}</Alert>
      )}
      {data.suggested_values.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="suggested-alert">
          {data.suggested_values.length} value(s) are the plan&apos;s suggested paper defaults, not confirmed rules (marked &quot;Suggested&quot;
          in Rules in Effect). Saving a field marks it confirmed.
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
              aria-label="DEMA-WMA Gainer sections"
              // tab labels: bold, blue, 2px larger than the MUI default (14px -> 16px)
              sx={{ "& .MuiTab-root": { fontWeight: 700, fontSize: "1rem", color: "primary.main" },
                    "& .MuiTab-root.Mui-selected": { color: "primary.main" } }}>
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} id={`dema-tab-${t.key}`} aria-controls={`dema-tabpanel-${t.key}`}
                 data-testid={`tab-${t.key}`} />
          ))}
        </Tabs>
      </Box>

      {TABS.map((t) => (show(t.key) ? (
        <Box key={t.key} role="tabpanel" id={`dema-tabpanel-${t.key}`} aria-labelledby={`dema-tab-${t.key}`}
             hidden={tab !== t.key} data-testid={`tabpanel-${t.key}`}>
          {t.key === "trading" && <TradingTab data={data} onChanged={() => void load()} />}
          {t.key === "backtest" && <DemaWmaBacktestCard timeframeMinutes={activeTf} versions={data.versions} />}
          {t.key === "compare" && (
            <>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                Compares a stored backtest with this page&apos;s PAPER trades only; live trades are never included.
              </Alert>
              <DemaWmaComparisonCard />
            </>
          )}
          {t.key === "rules" && (
            <DemaWmaRulesTab overview={data} onSaved={async (msg) => { setNotice(msg); await load(); }}
                             onActivate={(v) => void activate(v)} onSaveV2Draft={() => void saveDraft()} />
          )}
        </Box>
      ) : null))}
    </Box>
  );
}
